
// ABOUTME: Payments page component for tracking referral bonuses and payment status
// ABOUTME: Displays payment statistics and referral payment table with filtering

import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useReferrals } from '@/hooks/useReferrals';
import { DollarSign, TrendingUp, Clock, Users, CreditCard } from 'lucide-react';

const Payments = () => {
  const { referrals, isLoading } = useReferrals();
  const [statusFilter, setStatusFilter] = useState('all');

  // Calculate payment statistics
  const stats = {
    totalBonuses: referrals?.reduce((sum, r) => sum + (r.bonus_amount || 0), 0) || 0,
    paidOut: referrals?.filter(r => r.bonus_paid).reduce((sum, r) => sum + (r.bonus_amount || 0), 0) || 0,
    pendingPayment: referrals?.filter(r => r.status === 'hired' && !r.bonus_paid).reduce((sum, r) => sum + (r.bonus_amount || 0), 0) || 0,
    successfulHires: referrals?.filter(r => r.status === 'hired').length || 0
  };

  // Filter referrals based on status
  const filteredReferrals = referrals?.filter(referral => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'paid') return referral.bonus_paid;
    if (statusFilter === 'pending') return referral.status === 'hired' && !referral.bonus_paid;
    if (statusFilter === 'not-due') return !['hired'].includes(referral.status);
    return true;
  }) || [];

  const getStatusBadge = (referral: any) => {
    if (referral.bonus_paid) {
      return <Badge variant="default" className="bg-green-100 text-green-800">Paid</Badge>;
    }
    if (referral.status === 'hired' && !referral.bonus_paid) {
      return <Badge variant="secondary" className="bg-orange-100 text-orange-800">Pending</Badge>;
    }
    return <Badge variant="outline" className="bg-white text-gray-800">Not Due</Badge>;
  };

  const getPaymentStatus = (referral: any) => {
    if (referral.bonus_paid) return 'Paid';
    if (referral.status === 'hired' && !referral.bonus_paid) return 'Pending';
    return 'Not Due';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading payments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Billing & Payments</h1>
          <p className="text-gray-600 mt-1">
            Track your performance incentives and payment status
          </p>
        </div>
        <Button className="bg-blue-600 hover:bg-blue-700 text-white">
          <CreditCard className="w-4 h-4 mr-2" />
          Process Payments
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Bonuses</p>
                <p className="text-2xl font-bold">${stats.totalBonuses}</p>
                <p className="text-xs text-gray-500 mt-1">All performance incentives</p>
              </div>
              <DollarSign className="w-8 h-8 text-gray-400" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Paid Out</p>
                <p className="text-2xl font-bold">${stats.paidOut}</p>
                <p className="text-xs text-gray-500 mt-1">Successfully paid</p>
              </div>
              <TrendingUp className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pending Payment</p>
                <p className="text-2xl font-bold">${stats.pendingPayment}</p>
                <p className="text-xs text-gray-500 mt-1">Awaiting payment</p>
              </div>
              <Clock className="w-8 h-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Successful Hires</p>
                <p className="text-2xl font-bold">{stats.successfulHires}</p>
                <p className="text-xs text-gray-500 mt-1">Hired candidates</p>
              </div>
              <Users className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Referral Payments Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Referral Payments</CardTitle>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40 bg-white text-black font-medium">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="not-due">Not Due</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="bg-white">
                <TableHead>Candidate</TableHead>
                <TableHead>Referrer</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Bonus</TableHead>
                <TableHead>Payment</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredReferrals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                    No payments found for the selected filter
                  </TableCell>
                </TableRow>
              ) : (
                filteredReferrals.map((referral) => (
                  <TableRow key={referral.id} className="hover:bg-gray-50">
                    <TableCell>
                      <div>
                        <p className="font-medium">{referral.candidate_name}</p>
                        <p className="text-sm text-gray-500">{referral.candidate_email}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-gray-600">
                      {referral.referrer_email}
                    </TableCell>
                    <TableCell>
                      <p className="text-sm">Role ID: {referral.job_role_id.slice(0, 8)}...</p>
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(referral)}
                    </TableCell>
                    <TableCell className="font-medium">
                      ${referral.bonus_amount || 0}
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant={referral.bonus_paid ? "default" : "outline"}
                        className={
                          referral.bonus_paid 
                            ? "bg-green-100 text-green-800" 
                            : referral.status === 'hired' 
                            ? "bg-orange-100 text-orange-800"
                            : "bg-white text-gray-800"
                        }
                      >
                        {getPaymentStatus(referral)}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default Payments;
